import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { ResponsiveButtonSizes } from "@/libraries/design/components/button/src/button";
import { ContentBlock } from "@/libraries/design/components/content-block/src/content-block";
import { Image } from "@/libraries/design/components/image/src/image";
import { ColumnLayout, wacToken } from "@/libraries/design/foundations";
import { RichTextComponent } from "@/libraries/design/utils";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

export interface LogoProps {
    src: string;
    alt: string;
    width?: number;
    href?: string;
    dataEventProps?: DataEventProps;
}

export interface IntegrationProps {
    title?: string;
    titleAs?: "h2" | "h3";
    titleAppearsAs?: "h2" | "h3";
    body?: string | React.ReactElement<typeof RichTextComponent>;
    linkLabel?: string;
    linkHref?: string;
    linkDataEventProps?: DataEventProps;
    logoLinkDataEventProps?: DataEventProps;
    clickableLogos?: boolean;
    logos: LogoProps[];
}

const IntegrationLogo = (
    props: LogoProps & {
        dataEventProps?: DataEventProps;
        clickable?: boolean;
    }
) => {
    const logoListItemStyles = css({
        margin: 0,
        width: "80px",
        height: "80px",
        aspectRatio: "1",
        [media.above.md]: {
            width: "100px",
            height: "100px"
        }
    });

    const logoLinkStyles = css({
        "height": "100%",
        "width": "100%",
        "&:hover": {
            // used values from token("elevation.shadow.overlay"), modified for filter drop-shadow to look the same (https://stackoverflow.com/questions/39575894/why-does-box-shadow-look-different-than-filter-drop-shadow)
            "-webkit-filter": `drop-shadow(0px 8px 6px #1E1F2126) drop-shadow(0px 0px 0.5px #1E1F214F)`,
            "filter": `drop-shadow(0px 8px 6px #1E1F2126) drop-shadow(0px 0px 0.5px #1E1F214F)`
        }
    });

    const logoImage = (
        <Image
            src={props.src}
            alt={props.alt}
            style={{
                width: "100%",
                height: "100%",
                objectFit: "contain"
            }}
        />
    );

    if (!props.clickable) {
        return <li css={logoListItemStyles}>{logoImage}</li>;
    }

    if (!props.href) {
        return null;
    }

    return (
        <li css={logoListItemStyles}>
            <a
                href={props.href}
                css={logoLinkStyles}
                {...(props.dataEventProps ?? {})}
            >
                {logoImage}
            </a>
        </li>
    );
};

export const Integration = ({
    title,
    titleAs = "h3",
    titleAppearsAs,
    body,
    linkLabel,
    linkHref,
    linkDataEventProps,
    logoLinkDataEventProps,
    clickableLogos = true,
    logos
}: IntegrationProps) => {
    const sectionStyles = css({
        width: "100%",
        background: wacToken("color.neutral.light100"),
        display: "flex",
        flexDirection: "column",
        gap: token("space.800"),
        paddingBlock: token("space.800"),
        [media.above.sm]: {
            gap: token("space.1000"),
            paddingBlock: token("space.1000")
        },
        [media.above.md]: {
            gap: "96px",
            paddingBlock: "96px"
        }
    });

    const responsiveButtonSize: ResponsiveButtonSizes = {
        mobile: "small",
        tablet: "small",
        desktop: "large",
        xl_desktop: "large"
    };

    return (
        <section css={sectionStyles}>
            <ColumnLayout layout="8">
                <ContentBlock
                    title={title}
                    titleAs={titleAs}
                    titleAppearsAs={titleAppearsAs}
                    text={body}
                    callToActionProps={
                        linkLabel && linkHref
                            ? {
                                  button: {
                                      href: linkHref
                                  },
                                  buttonText: linkLabel,
                                  buttonVariant: "secondary",
                                  responsiveSize: responsiveButtonSize,
                                  ...(linkDataEventProps && {
                                      dataEventProps: linkDataEventProps
                                  })
                              }
                            : undefined
                    }
                    alignment={{
                        mobile: "center",
                        tablet: "center",
                        desktop: "center"
                    }}
                />
            </ColumnLayout>
            <ColumnLayout layout="12">
                <ul css={logosContainerStyles}>
                    {logos.map((logo, index) => (
                        <IntegrationLogo
                            key={index}
                            {...logo}
                            dataEventProps={
                                logo.dataEventProps ?? logoLinkDataEventProps
                            }
                            clickable={clickableLogos}
                        />
                    ))}
                </ul>
            </ColumnLayout>
        </section>
    );
};

const logosContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    marginInline: "auto",
    listStyleType: "none",
    padding: 0,
    justifyContent: "center",
    alignItems: "center",
    rowGap: token("space.300"),
    columnGap: token("space.400")
});
